import { Button, Card } from "antd";
import { useEffect, useState, useRef } from "react";
import {
  AddNewUser,
  RolePermissionModal,
  RolesTab,
  UsersTab,
} from "./component";
import apiService from "../../services/apiService";
import {
  Layers3,
  Plus,
  Search,
  ShieldHalf,
  UserCheck,
  UserRound,
  UserX,
  UsersRound,
} from "lucide-react";
import styles from "./manageRole.module.css";

interface teamCount {
  teamName: string | undefined;
  userCount: string;
}

const ManageRole = () => {
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const [selectedKpi, setSelectedKpi] = useState<number | null>(0);
  const [count, setCount] = useState<teamCount[]>([
    {
      teamName: "",
      userCount: "",
    },
    {
      teamName: "",
      userCount: "",
    },
    {
      teamName: "",
      userCount: "",
    },
    {
      teamName: "",
      userCount: "",
    },
    {
      teamName: "",
      userCount: "",
    },
  ]);

  useEffect(() => {
    getTilesData();
  }, []);

  const [addNewUserModal, setAddNewUserModal] = useState(false);
  const [addNewRoleModal, setAddNewRoleModal] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<any>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const closeUserModel = () => {
    setAddNewUserModal(false);
  };

  const closeRoleModel = () => {
    setAddNewRoleModal(false);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchText(""); // Reset search when switching tabs
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const openModal = () => {
    if (activeTab === "1") {
      setAddNewUserModal(true);
    } else if (activeTab === "2") {
      setAddNewRoleModal(true);
    }
  };

  const getTilesData = async () => {
    try {
      const response: any = await apiService.get("/users/team-counts");
      setCount(response);
    } catch (e) {
    }
  };

  const handleUsersUpdated = () => {
    setUsersRefreshKey((prev) => prev + 1);
    getTilesData();
  };

  const summaryConfig = [
    {
      title: count[0]?.teamName || "Total Users",
      value: Number(count[0]?.userCount) || 0,
      description: count[0]?.teamName || "Overview of user capacity",
      accent: "#2f8d8f",
      tint: "#eef8f8",
      border: "#d7e7ea",
      icon: <UsersRound size={16} strokeWidth={2.2} />,
    },
    {
      title: count[1]?.teamName || "Super Admins",
      value: Number(count[1]?.userCount) || 0,
      description: count[1]?.teamName || "Full access team",
      accent: "#17a34a",
      tint: "#ecfdf3",
      border: "#dbece2",
      icon: <UserCheck size={16} strokeWidth={2.2} />,
    },
    {
      title: count[2]?.teamName || "Teams",
      value: Number(count[2]?.userCount) || 0,
      description: count[2]?.teamName || "Active teams",
      accent: "#8b5cf6",
      tint: "#f4edff",
      border: "#e5d9f1",
      icon: <Layers3 size={16} strokeWidth={2.2} />,
    },
    {
      title: count[3]?.teamName || "Active Users",
      value: Number(count[3]?.userCount) || 0,
      description: count[3]?.teamName || "Currently active",
      accent: "#3366ff",
      tint: "#eef3ff",
      border: "#d8e1f5",
      icon: <UserRound size={16} strokeWidth={2.2} />,
    },
    {
      title: count[4]?.teamName || "Inactive Users",
      value: Number(count[4]?.userCount) || 0,
      description: count[4]?.teamName || "Currently inactive",
      accent: "#db2777",
      tint: "#fff0f7",
      border: "#ead7e2",
      icon: <UserX size={16} strokeWidth={2.2} />,
    },
  ];

  return (
    <div className={styles.pageShell}>
      <header className={styles.fullHeader}>
        <div className={styles.headerInnerWrap}>
          <div className="relative flex flex-col gap-4 xl:flex-row xl:items-center">
            <div className="flex items-center gap-2.5 xl:min-w-[260px]">
              <div className="p-2 bg-[#eef7f8] rounded-2xl">
                <ShieldHalf className="h-4 w-4 text-[#5da8b4]" />
              </div>
              <div>
                <h1 className="text-[20px] leading-none font-semibold text-slate-800 tracking-tight">
                  Manage Team
                </h1>
                <p className="mt-1 text-[11px] text-slate-500">
                  Manage users, teams & permissions
                </p>
              </div>
            </div>
            <div className="xl:absolute xl:left-1/2 xl:-translate-x-1/2">
              <div className="inline-flex h-auto items-center rounded-2xl border border-slate-200 bg-slate-50/90 p-1 shadow-[0_8px_30px_rgba(15,23,42,0.05)]">
                <button
                  type="button"
                  onClick={() => handleTabChange("1")}
                  className={`group inline-flex min-w-[130px] items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium transition-all duration-200 ${activeTab === "1"
                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-[#5da8b4]/15"
                    : "text-slate-500"
                    }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-md transition-all ${activeTab === "1"
                      ? "bg-[#e7f6f8] text-[#4d9aa6]"
                      : "bg-white text-slate-400"
                      }`}
                  >
                    <UsersRound className="h-3 w-3" />
                  </span>
                  Users
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange("2")}
                  className={`group inline-flex min-w-[130px] items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium transition-all duration-200 ${activeTab === "2"
                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-[#5da8b4]/15"
                    : "text-slate-500"
                    }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-md transition-all ${activeTab === "2"
                      ? "bg-[#e7f6f8] text-[#4d9aa6]"
                      : "bg-white text-slate-400"
                      }`}
                  >
                    <ShieldHalf className="h-3 w-3" />
                  </span>
                  Roles
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className={styles.bodyRoot}>
        <Card className={styles.kpiCard} bodyStyle={{ padding: 0 }}>
          <div className={styles.summaryGrid}>
            {summaryConfig.map((item, index) => (
              <Card
                key={`${item.title}-${index}`}
                className={`${styles.summaryCard} ${selectedKpi === index ? styles.summaryCardActive : ""
                  }`}
                onClick={() => setSelectedKpi(index)}
                style={{
                  borderColor:
                    selectedKpi === index ? "transparent" : item.border,
                  background:
                    selectedKpi === index
                      ? ""
                      : `linear-gradient(135deg, #ffffff 0%, #ffffff 50%, ${item.tint} 100%)`,
                  ["--accent" as string]: item.accent,
                  ["--tint" as string]: item.tint,
                }}
                bodyStyle={{ padding: 0 }}
                hoverable
              >
                <div className={styles.summaryCardInner}>
                  <div>
                    <div className={styles.summaryLabel}>{item.title}</div>
                    <div className={styles.summaryValue}>{item.value}</div>
                  </div>
                  <div className={styles.summaryMeta}>{item.description}</div>
                  <div className={styles.summaryIcon}>{item.icon}</div>
                </div>
              </Card>
            ))}
          </div>
        </Card>

        <Card className={styles.toolbarCard} bodyStyle={{ padding: 0 }}>
          <div className={styles.toolbarInner}>
            <div className={styles.searchWrapper}>
              <Search
                className={`${styles.searchIcon} ${isSearchFocused ? styles.searchIconActive : ""}`}
                size={16}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder={activeTab === "1" ? "Search users..." : "Search roles..."}
                className={styles.rawSearchInput}
              />
              <div className={styles.searchShortcut}>
                <span className="text-[10px]">Ctrl</span> K
              </div>
            </div>
            <Button
              className={styles.primaryAction}
              type="primary"
              onClick={() => openModal()}
            >
              <Plus size={16} strokeWidth={2.4} />
              {activeTab === "1" ? "Add New User" : "Add New Role"}
            </Button>
          </div>
        </Card>

        <div className={styles.contentShell}>
          {activeTab === "1" ? (
            <UsersTab searchText={searchText} refreshKey={usersRefreshKey} />
          ) : (
            <RolesTab searchText={searchText} />
          )}
        </div>
      </div>
      <AddNewUser
        addNewUserModal={addNewUserModal}
        closeModel={closeUserModel}
        getUser={handleUsersUpdated}
      />
      <RolePermissionModal
        openEditModal={addNewRoleModal}
        closeModal={closeRoleModel}
      />
    </div>
  );
};

export default ManageRole;
